import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import {
  adminClientsService,
  type ClientDetail,
  type ClientStoragePatch,
} from '../../../services/adminClientsService';
import { toastSuccess, toastError } from '../../../lib/toast';
import type { AutonomousTabHandle } from './IdentityTab';

interface Props {
  clientId: string;
  initialData: ClientDetail;
  onSaved?: (updated: ClientDetail) => void;
}

interface LocalState {
  storage_backend: string;
  storage_config: Record<string, any>;
}

const toLocal = (c: ClientDetail): LocalState => ({
  storage_backend: c.storage_backend,
  storage_config: c.storage_config ? { ...c.storage_config } : {},
});

export const StorageTab = forwardRef<AutonomousTabHandle, Props>(
  ({ clientId, initialData, onSaved }, ref) => {
    const [state, setState] = useState<LocalState>(() => toLocal(initialData));
    const [baseline, setBaseline] = useState<LocalState>(() => toLocal(initialData));
    const [saving, setSaving] = useState(false);
    const [showSecret, setShowSecret] = useState(false);
    const [testing, setTesting] = useState(false);
    const [storedCount, setStoredCount] = useState<number | null>(null);

    const isDirty = JSON.stringify(state) !== JSON.stringify(baseline);
    // PROD1.5.6 — déclenche un warning si on touche à un champ qui change la
    // localisation physique des fichiers. On ignore volontairement les credentials
    // (access_key/secret_key, region) : changer juste les creds ne déplace pas
    // les objets, donc pas de risque de perte de visibilité.
    const LOCATION_KEYS = ['bucket', 'key_prefix', 'endpoint_url'] as const;
    const backendChanged = state.storage_backend !== baseline.storage_backend;
    const locationChanged = LOCATION_KEYS.some(
      (k) => (state.storage_config?.[k] ?? '') !== (baseline.storage_config?.[k] ?? '')
    );
    const showMigrationWarning =
      (backendChanged || locationChanged) && (storedCount ?? 0) > 0;

    useEffect(() => {
      let cancelled = false;
      adminClientsService
        .getStorageStats(clientId)
        .then((s) => {
          if (!cancelled) setStoredCount(s.total_stored_objects);
        })
        .catch(() => {
          if (!cancelled) setStoredCount(null);
        });
      return () => {
        cancelled = true;
      };
    }, [clientId]);

    useImperativeHandle(ref, () => ({
      getIsDirty: () => isDirty,
      reset: () => setState(baseline),
    }));

    const cfg = state.storage_config;
    const updateConfig = (patch: Record<string, any>) => {
      setState((s) => ({ ...s, storage_config: { ...s.storage_config, ...patch } }));
    };

    const handleSave = async () => {
      if (!isDirty) return;
      if (showMigrationWarning) {
        const confirmed = window.confirm(
          `Vous changez la localisation du storage alors que ce tenant a ${storedCount} objet(s) référencé(s) en BD.\n\n` +
            `Les fichiers physiquement stockés à l'ancienne localisation ne seront PAS migrés automatiquement et deviendront inaccessibles via Dokimo.\n\n` +
            `Procédure recommandée : effectuer un \`mc mirror\` (ou équivalent) du contenu ancien → nouvelle localisation AVANT d'enregistrer. Voir docs/STORAGE_MIGRATION_GUIDE.md.\n\n` +
            `Continuer quand même ?`
        );
        if (!confirmed) return;
      }
      setSaving(true);
      try {
        const payload: ClientStoragePatch = {};
        if (state.storage_backend !== baseline.storage_backend) {
          payload.storage_backend = state.storage_backend;
        }
        if (JSON.stringify(state.storage_config) !== JSON.stringify(baseline.storage_config)) {
          payload.storage_config = state.storage_config;
        }
        const updated = await adminClientsService.patchStorage(clientId, payload);
        setBaseline(toLocal(updated));
        setState(toLocal(updated));
        toastSuccess('Storage enregistré');
        onSaved?.(updated);
      } catch (err) {
        toastError('Enregistrement échoué', err);
      } finally {
        setSaving(false);
      }
    };

    const handleTest = async () => {
      setTesting(true);
      try {
        const result = await adminClientsService.testStorage(clientId);
        if (result.ok) {
          toastSuccess('Connexion OK');
        } else {
          toastError(`Échec : ${result.error || 'erreur inconnue'}`);
        }
      } catch (err) {
        toastError('Test échoué', err);
      } finally {
        setTesting(false);
      }
    };

    return (
      <div className="space-y-4 max-w-2xl">
        {showMigrationWarning && (
          <div className="rounded bg-amber-50 border border-amber-300 px-3 py-2 text-sm text-amber-900">
            <strong>⚠ Changement de localisation du storage détecté</strong>{' '}
            ({backendChanged ? 'backend' : ''}
            {backendChanged && locationChanged ? ' + ' : ''}
            {locationChanged ? 'bucket/key_prefix/endpoint' : ''}). Ce tenant a{' '}
            <strong>{storedCount}</strong> objet(s) référencé(s) en BD (documents +
            ground truth). Les fichiers physiquement stockés à l'ancienne
            localisation ne seront <strong>pas migrés automatiquement</strong> —
            ils deviendront inaccessibles via l'application sans{' '}
            <code>mc mirror</code> préalable. Voir{' '}
            <code>docs/STORAGE_MIGRATION_GUIDE.md</code>.
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Backend de stockage</label>
          <select
            value={state.storage_backend}
            onChange={(e) => setState((s) => ({ ...s, storage_backend: e.target.value }))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="minio_local">MinIO local</option>
            <option value="s3_remote">S3 distant</option>
          </select>
        </div>

        {state.storage_backend === 's3_remote' && (
          <div className="border border-gray-200 rounded p-4 space-y-3 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Credentials S3</h3>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Endpoint</label>
              <input
                type="text"
                value={cfg.endpoint_url || ''}
                onChange={(e) => updateConfig({ endpoint_url: e.target.value })}
                placeholder="https://nyc3.digitaloceanspaces.com"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Région</label>
                <input
                  type="text"
                  value={cfg.region_name || ''}
                  onChange={(e) => updateConfig({ region_name: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Bucket</label>
                <input
                  type="text"
                  value={cfg.bucket || ''}
                  onChange={(e) => updateConfig({ bucket: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Access key</label>
              <input
                type="text"
                value={cfg.aws_access_key_id || ''}
                onChange={(e) => updateConfig({ aws_access_key_id: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Secret key</label>
              <div className="flex gap-2">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={cfg.aws_secret_access_key || ''}
                  onChange={(e) => updateConfig({ aws_secret_access_key: e.target.value })}
                  placeholder="(non modifié)"
                  className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret((v) => !v)}
                  className="px-3 py-2 text-xs border border-gray-300 rounded hover:bg-gray-100"
                >
                  {showSecret ? 'Masquer' : 'Afficher'}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Préfixe (key_prefix)</label>
              <input
                type="text"
                value={cfg.key_prefix || ''}
                onChange={(e) => updateConfig({ key_prefix: e.target.value })}
                placeholder={`dokimmo/${initialData.slug}/`}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
              />
            </div>
          </div>
        )}

        <div className="pt-2 border-t border-gray-200">
          {isDirty && (
            <p className="text-xs text-amber-700 mb-2">
              Enregistrez avant de tester — le test utilise la configuration persistée en BD.
            </p>
          )}
          <button
            type="button"
            onClick={handleTest}
            disabled={isDirty || testing}
            className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testing ? 'Test en cours...' : 'Tester la connexion'}
          </button>
        </div>

        <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-white border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saving}
            className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    );
  },
);

StorageTab.displayName = 'StorageTab';

export default StorageTab;
